/**
 * @fileoverview Sort Companies Cards Component
 * @active Used in current Next.js App Router routes
 *
 * This component provides sorting functionality for the new companies card view.
 * It renders a dropdown menu with sorting options (A-Z, Z-A) and a clear option.
 *
 * Key functionality:
 * - Displays a sort icon that opens a dropdown menu
 * - Provides options to sort companies by name in ascending or descending order
 * - Updates Redux state with sorting parameters
 * - Triggers API requests with updated sorting parameters
 * - Provides a clear option to remove sorting
 *
 * This component is used in the NewCompaniesCards component to allow users
 * to sort the displayed company cards.
 *
 * @see {@link src/components/companies/newCompaniesCards/index.tsx} - Parent component
 * @see {@link src/hooks/useCompaniesListRequest.js} - Hook for API requests
 * @see {@link src/store/filters/filters.selectors.ts} - Redux selectors for filters
 * @see {@link src/store/filters/filters.slice.ts} - Redux slice for filters
 */

import { useSelector } from "react-redux";

import {
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";

import SortBySVG from "@/assets/svg/sort_by.svg";
import { useActions } from "@/hooks/useActions";
import useCompaniesListRequest from "@/hooks/useCompaniesListRequest";
import filtersSelectors from "@/store/filters/filters.selectors";

/**
 * Sort options for the companies list
 * @type {Array<{id: number, title: string, key: string}>}
 */
const list = [
  { id: 1, title: "Name: A-Z", key: "ASC" },
  { id: 2, title: "Name: Z-A", key: "DESC" },
];

/**
 * Sort Companies Cards component
 *
 * This component provides a dropdown menu for sorting companies by name
 * in either ascending or descending order. It also includes a clear option
 * to remove sorting.
 *
 * @returns {JSX.Element} Rendered component
 *
 * @example
 * <SortCompaniesCards />
 */
const SortCompaniesCards = () => {
  const { getCompanies } = useCompaniesListRequest();
  const { setParamsForCompaniesCard } = useActions();
  const reqParams = useSelector(filtersSelectors.getParamsForCompaniesCards());

  const onSortHandler = (key) => {
    setParamsForCompaniesCard({
      ...reqParams,
      orderBy: "nameBrand",
      direction: key,
    });
    getCompanies({
      params: {
        ...reqParams,
        limit: 24,
        page: 1,
        orderBy: "nameBrand",
        direction: key,
      },
    });
  };

  const onClearHandler = () => {
    getCompanies({
      params: { ...reqParams, orderBy: undefined, direction: undefined },
    });
  };

  return (
    <Menu>
      {({ isOpen }) => (
        <>
          <MenuButton>
            <Flex align={"center"} columnGap={"5px"}>
              <Text
                fontSize={"14px"}
                fontWeight={"400"}
                lineHeight={"1.35"}
                letterSpacing={"2%"}
                mb={"-4px"}
                display={{ base: "block", md: "none" }}
              >
                Sort By:
              </Text>
              <Icon as={SortBySVG} cursor={"pointer"} boxSize={"24px"} />
            </Flex>
          </MenuButton>
          {isOpen ? (
            <MenuList
              minW={"fit-content"}
              p={"12px"}
              fontSize={"16px"}
              fontWeight={"700"}
              lineHeight={"1"}
              letterSpacing={"0.32px"}
            >
              {list.map((item) => (
                <MenuItem
                  key={item?.id}
                  p={"0px"}
                  py={"8px"}
                  bg={"transparent"}
                  onClick={() => onSortHandler(item?.key)}
                  _hover={{ bg: "transparent" }}
                >
                  <Flex
                    gap={"12px"}
                    borderBottom={"2px solid"}
                    borderColor={"transparent"}
                    transition={"all 0.3s ease"}
                    _hover={{ borderColor: "red" }}
                  >
                    <Text>{item?.title}</Text>
                  </Flex>
                </MenuItem>
              ))}
              <MenuItem
                p={"0px"}
                py={"8px"}
                bg={"transparent"}
                _hover={{ bg: "transparent" }}
              >
                <Text
                  borderBottom={"2px solid"}
                  borderColor={"transparent"}
                  transition={"all 0.3s ease"}
                  onClick={onClearHandler}
                  _hover={{ borderColor: "red" }}
                >
                  Clear
                </Text>
              </MenuItem>
            </MenuList>
          ) : null}
        </>
      )}
    </Menu>
  );
};

export default SortCompaniesCards;
